'use client';

import { mergeProps, useId, useObjectRef } from '@react-aria/utils';
import clsx from 'clsx';
import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef } from '../../forward-ref.js';
import type { CommonProps } from '../types.js';
import { Text } from '../typography/text.js';
import { useColumnData, useEmptyStateCell } from './contexts/column-collection.js';
import { TableRowStateProvider, useTableRowState } from './contexts/row-state.js';
import { useTableCell } from './hooks/use-table-cell.js';

export interface TableEmptyStateProps extends CommonProps {
  /** The title of the empty state. */
  title: string;
  /** The description of the empty state. */
  description: string;
  /** An optional action button displayed within the empty state. */
  action?: ReactNode;
}

function TableEmptyState(props: TableEmptyStateProps, ref: ForwardedRef<HTMLTableCellElement>) {
  const rowKey = useId();

  return (
    <TableRowStateProvider rowKey={rowKey}>
      <TableEmptyStateInner props={props} forwardedRef={ref} />
    </TableRowStateProvider>
  );
}

/**
 * Renders an empty state within a table that has no data.
 *
 * See [table usage guidelines](https://ui.cimpress.io/components/table/) and [table building guide](https://ui.cimpress.io/dev-guides/tables/).
 */
const _TableEmptyState = forwardRef(TableEmptyState, 'TableEmptyState');

interface TableEmptyStateInnerProps {
  props: TableEmptyStateProps;
  forwardedRef: ForwardedRef<HTMLTableCellElement>;
}

function TableEmptyStateInner({
  props: { UNSAFE_className, UNSAFE_style, title, description, action, ...props },
  forwardedRef,
}: TableEmptyStateInnerProps) {
  const cellRef = useObjectRef(forwardedRef);
  const { columnKey, colSpan } = useEmptyStateCell(cellRef);
  const { rowKey } = useTableRowState();
  // `useTableCell` requires `TableRowStateProvider`
  const { cellProps } = useTableCell(cellRef, rowKey, columnKey);
  const columnData = useColumnData(columnKey);

  return (
    <tr key="__cim__internal__empty-state__">
      <td
        {...mergeProps(props, cellProps)}
        ref={cellRef}
        className={clsx('cim-table-empty-state', UNSAFE_className)}
        style={UNSAFE_style}
        aria-colindex={columnData?.number}
        colSpan={colSpan}
        data-alignment="center"
      >
        <div className="cim-table-empty-state-content">
          <Text as="span" variant="title-6" tone="base">
            {title}
          </Text>

          <Text as="span" variant="medium" tone="base">
            {description}
          </Text>

          {action && <div className="cim-table-empty-state-action">{action}</div>}
        </div>
      </td>
    </tr>
  );
}

export { _TableEmptyState as TableEmptyState };
